import { useState } from 'react'
import { api, type KorrekturAngebot, type PruefFrage } from '../api'
import { link } from '../navigation'
import { KorrekturAngebote } from './Korrektur'

type Beleg = NonNullable<PruefFrage['daten']['aussagen'][number]['beleg']>

function belegLink(b: Beleg): string | null {
  if (b.art === 'ICH') return link.ueberMich
  if (b.art === 'PERSON' && b.id) return link.person(b.id)
  if (b.art === 'NOTIZ' && b.id) return link.notiz(b.id)
  if (b.art === 'GETEILT' && b.id) return link.geteilt(b.id)
  return null
}

/** Rückfragen der Stichhaltigkeitsprüfung: abweichende Schreibweisen und Widersprüche zwischen Notizen */
export function PruefFragen({ fragen, onGeklaert }: { fragen: PruefFrage[]; onGeklaert: () => void }) {
  return (
    <section className="klaerungen">
      <h2>{fragen.length === 1 ? 'Eine Unstimmigkeit' : `${fragen.length} Unstimmigkeiten`} in deinen Notizen</h2>
      {fragen.map((f) => (
        <PruefFrageKarte key={f.id} frage={f} onGeklaert={onGeklaert} />
      ))}
    </section>
  )
}

function PruefFrageKarte({ frage: f, onGeklaert }: { frage: PruefFrage; onGeklaert: () => void }) {
  const [angebot, setAngebot] = useState<KorrekturAngebot | null>(null)
  const [anders, setAnders] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const aktion = async (fn: () => Promise<unknown>, danach: () => void = onGeklaert) => {
    setLaeuft(true)
    setFehler(null)
    try {
      await fn()
      danach()
    } catch (e) {
      setFehler((e as Error).message)
    }
    setLaeuft(false)
  }

  // Nach der Wahl der richtigen Schreibweise: Korrektur der anderen Notizen anbieten
  if (angebot) return <KorrekturAngebote angebote={[angebot]} onFertig={onGeklaert} frage="In deinen Notizen korrigieren?" weitere={false} />

  const datum = new Date(f.aufgenommenAm).toLocaleDateString('de-DE', { timeZone: f.zeitzone, weekday: 'short', day: 'numeric', month: 'numeric' })

  return (
    <article className="karte klaerung pruefung">
      <p>
        <strong>{f.daten.frage}</strong>
      </p>
      <p className="leise klein">
        Aufgefallen bei der <a href={link.notiz(f.notizId)}>Notiz vom {datum}</a>
      </p>
      {f.daten.aussagen.length > 0 && (
        <ul className="aussagen">
          {f.daten.aussagen.map((a, i) => {
            const ziel = a.beleg && belegLink(a.beleg)
            return (
              <li key={i}>
                <span>{a.text}</span>
                {a.beleg && (
                  <span className="leise klein">
                    {' '}
                    – {ziel ? <a href={ziel}>{a.beleg.titel}</a> : a.beleg.titel}
                  </span>
                )}
                {f.daten.art === 'WIDERSPRUCH' && (
                  <button className="link" disabled={laeuft} onClick={() => aktion(() => api.pruefKlarstellen(f.id, a.text))}>
                    Das stimmt
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {fehler && <p className="fehler">{fehler}</p>}

      {f.daten.art === 'SCHREIBWEISE' ? (
        <div className="auswahl">
          {f.daten.varianten.map((v) => (
            <button key={v} disabled={laeuft} onClick={() => aktion(async () => setAngebot(await api.pruefSchreibweise(f.id, v)), () => {})}>
              „{v}“ ist richtig
            </button>
          ))}
          <button className="zweitrangig" disabled={laeuft} onClick={() => aktion(() => api.pruefVerschieden(f.id))}>
            Beides richtig
          </button>
          <button className="link" disabled={laeuft} onClick={() => aktion(() => api.pruefIgnorieren(f.id))}>
            Ignorieren
          </button>
        </div>
      ) : (
        <>
          <form
            className="klarstellung"
            onSubmit={(e) => {
              e.preventDefault()
              if (anders.trim()) aktion(() => api.pruefKlarstellen(f.id, anders.trim()))
            }}
          >
            <input value={anders} maxLength={2000} placeholder="Oder richtigstellen, z. B. „Seit August in Hamburg“" onChange={(e) => setAnders(e.target.value)} />
            <button type="submit" disabled={laeuft || !anders.trim()}>
              Klarstellen
            </button>
          </form>
          <div className="auswahl">
            <button className="link" disabled={laeuft} onClick={() => aktion(() => api.pruefIgnorieren(f.id))}>
              Kein Widerspruch
            </button>
          </div>
          <p className="leise klein">Deine Antwort wird als Klarstellung gespeichert und gilt bei Fragen vor älteren Angaben.</p>
        </>
      )}
    </article>
  )
}
